import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';

export function ContactPage() {
  const { storeInfo } = useStoreInfo();
  const pageTitle = `Contact Us${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const phoneNumbers = storeInfo?.phone_num ? storeInfo.phone_num.split(',').map(num => num.trim()) : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://marivieint.com/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          domain: window.location.hostname
        }),
      });

      const data = await response.json();

      if (data.status) {
        toast.success('Message sent successfully!');
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Get in touch with us" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-brand-primary py-24">
          <div className="absolute inset-0">
            <img 
              src="/meta.jpg"
              alt="Contact background"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display">
              Contact Us
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6 font-display text-gray-900 dark:text-white">
                Send us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-2xl border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="Your message"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6 font-display text-gray-900 dark:text-white">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  {storeInfo?.contact_email && (
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-brand-50 dark:bg-brand-primary/20 rounded-full">
                          <Mail className="h-6 w-6 text-brand-primary dark:text-brand-light" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                        <a
                          href={`mailto:${storeInfo.contact_email}`}
                          className="text-lg font-medium text-gray-900 dark:text-white hover:text-brand-primary dark:hover:text-brand-light"
                        >
                          {storeInfo.contact_email}
                        </a>
                      </div>
                    </div>
                  )}

                  {phoneNumbers.length > 0 && (
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 pt-1">
                        <div className="p-3 bg-brand-50 dark:bg-brand-primary/20 rounded-full">
                          <Phone className="h-6 w-6 text-brand-primary dark:text-brand-light" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                        <div className="space-y-2">
                          {phoneNumbers.map((number, index) => (
                            <a
                              key={index}
                              href={`tel:${number.replace(/\s+/g, '')}`}
                              className="block text-lg font-medium text-gray-900 dark:text-white hover:text-brand-primary dark:hover:text-brand-light"
                            >
                              {number}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {storeInfo?.shop_address && (
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-brand-50 dark:bg-brand-primary/20 rounded-full">
                          <MapPin className="h-6 w-6 text-brand-primary dark:text-brand-light" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Address</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {storeInfo.shop_address}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="p-3 bg-brand-50 dark:bg-brand-primary/20 rounded-full">
                        <Clock className="h-6 w-6 text-brand-primary dark:text-brand-light" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Business Hours</p>
                      <div className="text-lg font-medium text-gray-900 dark:text-white">
                        <p>Mon - Sat: 10:00 AM - 8:00 PM</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {storeInfo?.google_map && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                  <div 
                    dangerouslySetInnerHTML={{ __html: storeInfo.google_map }}
                    className="w-full h-[300px]"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}